import React, { useState, useCallback } from 'react';
import {
  getMessageNotifyVolume,
  setMessageNotifyVolume,
  getMessageNotifyPreset,
  setMessageNotifyPreset,
  MESSAGE_NOTIFY_PRESETS,
  type MessageNotifyPresetId,
} from '../utils/messageNotificationSoundSettings';
import { previewMessageNotificationSound } from '../utils/playMessageNotificationSound';
import './MessageNotificationSoundControls.css';

type Variant = 'cweb' | 'cmob' | 'menu';

interface Props {
  variant: Variant;
  /** Atualiza o ícone do header (ex.: mudo quando 0). */
  onVolumePctChange?: (pct: number) => void;
}

/** SVG inline: evita ícone invisível (herança de cor / flex) no dropdown do header. */
function MsgNotifyVolumeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M11 5L6 9H2v6h4l5 4V5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a9 9 0 010 14.14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export const MessageNotificationSoundControls: React.FC<Props> = ({ variant, onVolumePctChange }) => {
  const [volumePct, setVolumePct] = useState(() => Math.round(getMessageNotifyVolume() * 100));
  const [preset, setPreset] = useState<MessageNotifyPresetId>(getMessageNotifyPreset);

  const selectClass =
    variant === 'menu'
      ? 'menu-message-sound-select msg-notify-select'
      : variant === 'cweb'
        ? 'cweb-control-select msg-notify-select'
        : 'cmob-menu-select msg-notify-select';

  const onPreset = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const id = e.target.value as MessageNotifyPresetId;
    setMessageNotifyPreset(id);
    setPreset(id);
  }, []);

  const onVolume = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      const pct = Number(e.target.value);
      setVolumePct(pct);
      setMessageNotifyVolume(pct / 100);
      onVolumePctChange?.(pct);
    },
    [onVolumePctChange]
  );

  const onTest = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    previewMessageNotificationSound();
  }, []);

  return (
    <div
      className={`msg-notify-controls${variant === 'menu' ? ' msg-notify-controls--menu' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="msg-notify-title">
        <MsgNotifyVolumeIcon className="msg-notify-icon msg-notify-icon--title" />
        <span className="msg-notify-title-text">Som de nova mensagem</span>
      </div>
      <label className="msg-notify-row msg-notify-stack" onClick={(e) => e.stopPropagation()}>
        <span className="msg-notify-sub">Tipo de alerta</span>
        <select
          className={selectClass}
          value={preset}
          onChange={onPreset}
          aria-label="Tipo de som de notificação"
        >
          {MESSAGE_NOTIFY_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="msg-notify-row msg-notify-stack" onClick={(e) => e.stopPropagation()}>
        <span className="msg-notify-sub msg-notify-volume-heading">
          <MsgNotifyVolumeIcon className="msg-notify-icon msg-notify-icon--inline" />
          Volume ({volumePct}%)
        </span>
        <input
          type="range"
          className="msg-notify-volume"
          min={0}
          max={100}
          value={volumePct}
          onChange={onVolume}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={volumePct}
          aria-label="Volume do som de notificação"
        />
      </label>
      <button type="button" className="msg-notify-test" onClick={onTest}>
        Testar som
      </button>
    </div>
  );
};
